import React, { useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './MapboxGlobe.scss';
import { splitAtAntimeridian, toMultiLineString } from '../../utils/antimeridian';

interface WaypointData {
  lat: number;
  lon: number;
}

export interface PortMarker {
  lat: number;
  lon: number;
  name: string;
  deviationNm?: number;
}

interface MapboxGlobeProps {
  waypoints: WaypointData[] | WaypointData[][];
  width?: string;
  height?: string;
  /** Named port pins rendered on top of the route (e.g. bunker ports). */
  ports?: PortMarker[];
  /** Draw a translucent corridor of this half-width (nm) around the route —
   *  the search radius band. Ground-width approximated at the route's mean
   *  latitude via zoom-exponential line width. */
  corridorNm?: number;
}

const MAPBOX_TOKEN = 'pk.eyJ1Ijoidm95Y2FsYyIsImEiOiJjbWpwbjdyemcxbTk0M2RzZTl3bWl6Z2liIn0.fdpp-x193yw-aPFm4P0jIQ';

const MapboxGlobe: React.FC<MapboxGlobeProps> = ({
  waypoints,
  width = '100%',
  height = '450px',
  ports,
  corridorNm
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (!mapContainer.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      projection: 'globe',
      zoom: 1.5,
      center: [0, 20],
    });

    // Add atmosphere styling for 3D globe effect
    map.current.on('style.load', () => {
      map.current?.setFog({
        color: 'rgb(18, 18, 18)',
        'high-color': 'rgb(41, 230, 255)',
        'horizon-blend': 0.02,
        'space-color': 'rgb(18, 18, 18)',
        'star-intensity': 0.4
      });
    });

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    return () => {
      map.current?.remove();
    };
  }, []);

  // Add waypoints and route line when data changes
  useEffect(() => {
    if (!map.current || waypoints.length === 0) return;

    const addRoute = () => {
      // Remove existing layers/sources if they exist
      if (map.current?.getSource('route')) {
        if (map.current.getLayer('route-corridor')) map.current.removeLayer('route-corridor');
        map.current.removeLayer('route-line');
        map.current.removeSource('route');
      }
      if (map.current?.getSource('waypoints')) {
        map.current.removeLayer('waypoint-circles');
        map.current.removeSource('waypoints');
      }
      if (map.current?.getSource('ports')) {
        map.current.removeLayer('port-labels');
        map.current.removeLayer('port-circles');
        map.current.removeSource('ports');
      }

      // Normalize to list-of-paths: backend returns flat array (IMOS / non-crossing
      // Dataloy) or array-of-paths (Dataloy crossing the antimeridian).
      const paths: WaypointData[][] = Array.isArray(waypoints[0])
        ? (waypoints as WaypointData[][])
        : [waypoints as WaypointData[]];

      // Split each path at the antimeridian and collect all segments into one
      // MultiLineString so the line renders correctly across the dateline.
      const allSegments = paths.flatMap(path => splitAtAntimeridian(path));
      const geometry = toMultiLineString(allSegments);

      // Add route line (using MultiLineString to handle antimeridian crossing)
      map.current?.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry
        }
      });

      // Flatten paths for waypoint markers and bounds. Endpoints are the very
      // first and very last point across all paths (voyage start/end).
      const flatPoints = paths.flat();

      // Radius band: a wide translucent line under the route. Line width is
      // in screen px, so approximate a constant ground width by anchoring the
      // px width at the route's mean latitude and doubling it per zoom level
      // (mapbox 512px tiles: ~78271.5 * cos(lat) m/px at zoom 0).
      if (corridorNm && corridorNm > 0 && flatPoints.length > 0) {
        const meanLat = flatPoints.reduce((s, p) => s + p.lat, 0) / flatPoints.length;
        const metersPerPxZ0 = 78271.484 * Math.cos((meanLat * Math.PI) / 180);
        const basePx = (corridorNm * 2 * 1852) / metersPerPxZ0;
        map.current?.addLayer({
          id: 'route-corridor',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#29e6ff',
            'line-opacity': 0.12,
            'line-width': [
              'interpolate', ['exponential', 2], ['zoom'],
              0, basePx,
              20, basePx * Math.pow(2, 20)
            ]
          }
        });
      }

      map.current?.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#29e6ff',
          'line-width': 3,
          'line-opacity': 0.8
        }
      });

      // Add waypoint circles
      map.current?.addSource('waypoints', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: flatPoints.map((wp, index) => ({
            type: 'Feature' as const,
            properties: {
              index,
              isEndpoint: index === 0 || index === flatPoints.length - 1
            },
            geometry: {
              type: 'Point' as const,
              coordinates: [wp.lon, wp.lat]
            }
          }))
        }
      });

      map.current?.addLayer({
        id: 'waypoint-circles',
        type: 'circle',
        source: 'waypoints',
        paint: {
          'circle-radius': [
            'case',
            ['get', 'isEndpoint'], 8,
            5
          ],
          'circle-color': [
            'case',
            ['get', 'isEndpoint'], '#ff6b6b',
            '#29e6ff'
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      });

      // Named port pins (e.g. bunker ports) on top of the route.
      if (ports && ports.length > 0) {
        map.current?.addSource('ports', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: ports.map(p => ({
              type: 'Feature' as const,
              properties: {
                label: p.deviationNm != null
                  ? `${p.name} (${Math.round(p.deviationNm)} nm)`
                  : p.name
              },
              geometry: {
                type: 'Point' as const,
                coordinates: [p.lon, p.lat]
              }
            }))
          }
        });

        map.current?.addLayer({
          id: 'port-circles',
          type: 'circle',
          source: 'ports',
          paint: {
            'circle-radius': 7,
            'circle-color': '#ffd166',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff'
          }
        });

        map.current?.addLayer({
          id: 'port-labels',
          type: 'symbol',
          source: 'ports',
          layout: {
            'text-field': ['get', 'label'],
            'text-size': 12,
            'text-offset': [0, 1.2],
            'text-anchor': 'top'
          },
          paint: {
            'text-color': '#ffd166',
            'text-halo-color': '#121212',
            'text-halo-width': 1.5
          }
        });
      }

      // Fit bounds to show all waypoints (and port pins, if any)
      if (flatPoints.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        flatPoints.forEach(wp => bounds.extend([wp.lon, wp.lat]));
        (ports || []).forEach(p => bounds.extend([p.lon, p.lat]));
        map.current?.fitBounds(bounds, { padding: 80, maxZoom: 5 });
      }
    };

    if (map.current.isStyleLoaded()) {
      addRoute();
    } else {
      map.current.on('load', addRoute);
    }
  }, [waypoints, ports, corridorNm]);

  return (
    <div
      ref={mapContainer}
      className="mapbox-globe-container"
      style={{ width, height }}
    />
  );
};

export default MapboxGlobe;
